import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  CHART_COLORS,
  CHART_TYPES,
  DEFAULT_RECHARTS_COLORS,
  getYAxisLabelConfig,
  shouldShowYAxisLabel,
} from '../constants';
import { useContainerSize } from '../../../../../../js/useContainerSize';
import CustomTick from '../CustomTick';
import CustomTooltip from '../CustomTooltip';
import EmptyState from '../Empty/EmptyState';
import { GROUP_OPTIONS } from '../../modals/chartCreationConstants';
import LoadingState from '../Loadinng/LoadingState';

const CHART_MARGIN = { top: 20, right: 30, left: 0, bottom: 10 };

const GRID_CONFIG = {
  strokeDasharray: '3 3',
  stroke: CHART_COLORS.GRID_STROKE,
  horizontal: true,
  vertical: false,
};

const AXIS_LINE_CONFIG = { stroke: CHART_COLORS.AXIS_STROKE };
const TICK_LINE_CONFIG = { stroke: CHART_COLORS.AXIS_STROKE };
const Y_TICK_CONFIG = { fill: CHART_COLORS.AXIS_STROKE, fontSize: 12 };

const X_AXIS_CONFIG = {
  angle: -45,
  textAnchor: 'end',
};

const LINE_CONFIG = {
  type: 'monotone',
  strokeWidth: 2,
  dot: { r: 4 },
  activeDot: { r: 6 },
};

const TOOLTIP_CONFIG = { fill: 'transparent' };

const StyledXAxis = ({ label, labelEntities, labelType }) => (
  <XAxis
    dataKey="name"
    {...X_AXIS_CONFIG}
    interval="preserveStartEnd"
    minTickGap={[GROUP_OPTIONS.ASSIGNEE, GROUP_OPTIONS.CREATOR].includes(labelType) ? 15 : undefined}
    height={
      [GROUP_OPTIONS.ASSIGNEE, GROUP_OPTIONS.CREATOR].includes(labelType)
        ? 30
        : 80
    }
    label={{ value: label, position: 'insideBottom', offset: -10 }}
    tick={(props) => (
      <CustomTick
        labelEntities={labelEntities}
        labelType={labelType}
        {...props}
      />
    )}
    axisLine={AXIS_LINE_CONFIG}
    tickLine={TICK_LINE_CONFIG}
  />
);

const StyledYAxis = ({ label }) => (
  <YAxis
    allowDecimals={false}
    axisLine={AXIS_LINE_CONFIG}
    tickLine={TICK_LINE_CONFIG}
    tick={Y_TICK_CONFIG}
    label={label ? getYAxisLabelConfig(label) : undefined}
  />
);

const LineGraph = ({
  chartData,
  isLoading,
  colors = DEFAULT_RECHARTS_COLORS,
  yAxisLabel = '',
  xAxisLabel = '',
  xLabelType,
  xLabelEntities,
  handleSliceClick = () => {},
}) => {
  const intl = useIntl();
  const wrapperRef = useRef(null);
  const [tooltipY, setTooltipY] = useState(undefined);
  // Callback ref, not `useEffect` + `useRef`: this component returns early
  // while loading/empty, so the wrapper div does not exist on mount — a
  // mount-time effect would find a null ref and never observe anything.
  const { ref: sizeRef, size: containerSize } = useContainerSize();

  const setWrapperRef = useCallback(
    (node) => {
      wrapperRef.current = node;
      sizeRef(node);
    },
    [sizeRef],
  );

  const handleMouseMove = useCallback((e) => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipY(e.clientY - rect.top + 15);
    }
  }, []);

  const handleMouseLeave = useCallback(() => setTooltipY(undefined), []);

  // Hidden when the card is too short for the rotated title to render
  // without being clipped / colliding with the top tick (see constants.js).
  const showYAxisLabel = shouldShowYAxisLabel(yAxisLabel, containerSize);

  if (isLoading) {
    return <LoadingState type={CHART_TYPES.LINE} />;
  }

  if (!chartData?.length) {
    return <EmptyState type={CHART_TYPES.LINE} />;
  }

  const tasksLabel = intl.formatMessage({
    id: 'TASKS',
    defaultMessage: 'Tasks',
  });

  return (
    <div ref={setWrapperRef} style={{ width: '100%', height: '100%' }} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={chartData} margin={CHART_MARGIN}>
        <CartesianGrid {...GRID_CONFIG} />
        <StyledXAxis
          label={xAxisLabel}
          labelType={xLabelType}
          labelEntities={xLabelEntities}
        />
        <StyledYAxis label={showYAxisLabel ? yAxisLabel : ''} />
        <Tooltip
          content={
            <CustomTooltip
              xLabelEntities={xLabelEntities}
              xLabelType={xLabelType}
              isStacked={false}
            />
          }
          cursor={TOOLTIP_CONFIG}
          allowEscapeViewBox={{ x: true, y: true }}
          wrapperStyle={{ zIndex: 9999 }}
          position={tooltipY !== undefined ? { y: tooltipY } : undefined}
        />
        <Line
          dataKey="value"
          name={tasksLabel}
          stroke={colors[0]}
          {...LINE_CONFIG}
          activeDot={{
            ...LINE_CONFIG.activeDot,
            cursor: 'pointer',
            onClick: (e, payload) =>
              handleSliceClick(payload?.payload, chartData),
          }}
        />
      </LineChart>
    </ResponsiveContainer>
    </div>
  );
};

export default LineGraph;
